import { createStore } from 'vuex'
import merchantFetch from '@/utils/merchantFetch'
import mapIncluded from '@/utils/mapIncluded'
import mapRelationships from '@/utils/mapRelationships'
import mapRelationship from '@/utils/mapRelationship'
import router from '@/router'
import { getPersistedUtmParams, isV2Override } from '@/router'
import { hasCapability } from '@/utils/capabilities'
import { ulid } from 'ulid'
import { getStorageItem, setStorageItem, removeStorageItem, parseStoredJSON, safeJwtDecode } from '@/utils/safeStorage'
import { franchiseStoreContextFromLocation } from '@/utils/franchiseStoreContext'
import { accountMembershipTiers, checkoutMembershipTiers, sortMembershipTiers } from '@/utils/membershipTierOptions'
import { posthog, getCommonTrackingProperties } from '@/composables/usePosthog'

// Everything the checkout page shows about the cart, re-read when the cart the page holds turns
// out to be out of date (SUPPORT-2992).
const CHECKOUT_CART_INCLUDES = [
  'checkout',
  'cart',
  'cart.pricing_tier',
  'cart.next_pricing_tier',
  'cart.available_pricing_tiers',
  'cart.cart_items',
  'cart.cart_items.product',
  'cart.cart_items.product.category_product_store_pairs',
  'cart.cart_items.product.attachments',
  'cart.cart_items.cart_item_variant_options',
  'cart.applied_discount_codes'
]

function trackFulfillmentOptionSelected(res, getters, state) {
  const fulfillmentSlotId = res.data?.relationships?.fulfillment_slot?.data?.id
  const fulfillmentMethodId = res.data?.relationships?.fulfillment_method?.data?.id

  const fulfillmentSlot = res.included?.find(
    (item) => item.type === 'fulfillment_slot' && item.id === fulfillmentSlotId?.toString()
  )
  const fulfillmentMethod = res.included?.find(
    (item) => item.type === 'fulfillment_method' && item.id === fulfillmentMethodId?.toString()
  )
  if (fulfillmentSlot && fulfillmentMethod) {
    const context = { getters: getters, state: state }
    const trackingProperties = {
      ...getCommonTrackingProperties(context),
      fulfillment_slot_id: fulfillmentSlot.attributes?.id,
      fulfillment_method_id: fulfillmentMethod.attributes?.id,
      fulfillment_method_type: fulfillmentMethod.attributes?.type,
      fulfillment_method_name: fulfillmentMethod.attributes?.name
    }
    posthog.capture('fulfillment_option_selected', trackingProperties)
  }
}

// Upsells are promise-cached per cart id, but selection-question answers
// (package autofill, fulfillment slot) mutate that same cart — drop the cache
// so MerchantMain/MerchantBasket refetch against the post-answer cart.
function invalidateUpsellsCache(state, commit) {
  for (const name of [...state.promises.keys()]) {
    if (name.startsWith('upsells/')) commit('DELETE_PROMISE', name)
  }
}

const store = createStore({
  state: {
    pendingItems: [],
    currentTokenMerchant: null,
    currentTokenCustomer: null,
    anonymousTokenSecret: null,
    conversationUserPairId: null,
    decodedJWT: null,
    franchiseId: null,
    selectedStoreSlug: getStorageItem('selectedStoreSlug'),
    dismissedLoggedInAsFor: null,
    storeAccessTokens: {},
    cartHandoffScrollY: null,
    // The bottle whose cart drawer was open when the customer last left the menu, so coming back
    // from checkout puts it back (BOT-1730).
    cartDrawerLeftOpenFor: null,
    cartController: new AbortController(),
    activelySubmittingCart: false,
    cartSuperseded: false,
    objects: {
      applied_discount_code: {},
      attachment: {},
      bottle: {},
      customer: {},
      delivery_address: {},
      fulfillment_slot: {},
      fulfillment_method: {},
      submittable_cart: {},
      cart: {},
      cart_item: {},
      checkout: {},
      customer: {},
      membership_tier: {},
      membership_tier_selection: {},
      cart_subscription: {},
      cart_subscription_setting: {},
      merchant: {},
      store: {},
      user: {},
      stripe_payment_method: {},
      stripe_payment_intent: {},
      category: {},
      category_product_store_pair: {},
      pricing_tier: {},
      question: {},
      answer: {},
      issued_gift_card: {},
      tip_amount: {},
      discount_code: {},
      referral_discount_program: {},
      package: {},
      page: {},
      page_module: {},
      form: {}
    },
    // the tiers the server offered for each bottle's store, keyed by bottle id
    bottleMembershipTierIds: {},
    splitDay: null,
    promises: new Map(),
    mainContentLoaded: false,
    facebookPixelInitialized: false
  },
  getters: {
    isLoggedIn: (state, getters) => {
      return state.currentTokenCustomer?.id
    },

    customer: (state, getters) => {
      return state.objects.customer[state.currentTokenCustomer?.id]
    },

    reseller: (state, getters) => {
      return getters.customer?.attributes?.reseller
    },

    impersonatingReseller: (state) => {
      return state.decodedJWT?.reseller
    },

    ownsBottle: (state, getters) => (bottle) => {
      return getters.isLoggedIn && bottle?.relationships?.customer?.data?.id == state.currentTokenCustomer?.id
    },

    isGiftRecipientOfBottle: (state, getters) => (bottle) => {
      return (
        getters.isLoggedIn &&
        bottle?.attributes?.gift_recipient_user_id != null &&
        bottle?.attributes?.gift_recipient_user_id == state.currentTokenCustomer?.id
      )
    },

    authorizedForBottle: (state, getters) => (bottle) => {
      if (bottle?.relationships?.customer?.data?.id) {
        return getters.ownsBottle(bottle) || getters.isGiftRecipientOfBottle(bottle)
      } else {
        return state.anonymousTokenSecret == bottle?.attributes?.anonymous_token
      }
    },

    bottleId: () => {
      return router.currentRoute.value.params.bottleid
    },

    mainContentLoaded: (state) => state.mainContentLoaded,

    currentBottle: (state, getters) => {
      const bottle = state.objects.bottle?.[getters.bottleId]
      if (bottle) {
        const store = mapRelationship(state, bottle.relationships.store.data)
        if (store && !store.attributes.is_active) {
          router.push({ name: 'Home' })
        }
      }
      return bottle
    },

    bottleWithID: (state, getters) => (bottleId) => {
      if (state.objects.bottle) return state.objects.bottle[parseInt(bottleId)]
    },

    currentBottles: (state, getters) => {
      if (state.objects.bottle) {
        const parentBottle = state.objects.bottle[getters.bottleId]
        if (parentBottle) {
          const bottles = parentBottle.relationships.children_bottles.data.map((obj) => {
            return state.objects.bottle[parseInt(obj.id)]
          })
          return bottles
        }
      }
    },

    getCustomer: (state, getters) => (relationshipHash) => {
      if (relationshipHash == null || state.objects.customer == null) {
        return null
      } else {
        return mapRelationship(state, relationshipHash)
      }
    },

    bottles: (state, getters) => (relationshipArray) => {
      if (relationshipArray == null || state.objects.bottle == null) {
        return null
      } else {
        return mapRelationships(state, relationshipArray)
      }
    },

    issuedGiftCards: (state, getters) => (relationshipArray) => {
      if (relationshipArray == null || state.objects.issued_gift_card == null) {
        return null
      } else {
        return mapRelationships(state, relationshipArray)
      }
    },

    currentCart: (state, getters) => {
      return mapRelationship(state, getters.currentBottle?.relationships?.cart?.data)
    },

    cart: (state, getters) => (relationshipHash) => {
      if (relationshipHash == null || state.objects.cart == null) {
        return null
      } else {
        return mapRelationship(state, relationshipHash)
      }
    },

    cartSize: (state, getters) => getters.submittableCart.reduce((acc, x) => acc + x.quantity, 0),

    appliedDiscountCodes: (state, getters) => (relationshipArray) => {
      if (relationshipArray == null || state.objects.applied_discount_code == null) {
        return null
      } else {
        return mapRelationships(state, relationshipArray)
      }
    },

    currentPricingTier: (state, getters) => {
      return mapRelationship(state, getters.currentCart?.relationships.pricing_tier?.data)
    },

    pricingTierWithID: (state, getters) => (pricingTierID) => {
      return state.objects.pricing_tier[pricingTierID]
    },

    availablePricingTiers: (state, getters) => {
      return mapRelationships(state, getters.currentCart?.relationships?.available_pricing_tiers?.data)
    },

    storeId: (state, getters) => getters.currentBottle?.relationships.store.data?.id,

    store: (state, getters) => (relationshipHash) => {
      if (relationshipHash == null || state.objects.store == null) {
        return null
      } else {
        return mapRelationship(state, relationshipHash)
      }
    },

    stores: (state, getters) => {
      if (!state.objects.store) return []
      return Object.values(state.objects.store)
    },

    rootPage: (state, getters) => {
      if (!state.objects.page) return null
      return Object.values(state.objects.page).find((x) => x.attributes.type === 'RootPage')
    },

    globalPage: (state, getters) => {
      if (!state.objects.page) return null
      return Object.values(state.objects.page).find((x) => x.attributes.type === 'GlobalPage')
    },

    page: (state, getters) => (route) => {
      if (!state.objects.page) return null
      return Object.values(state.objects.page).find((x) => x.attributes.route === route)
    },

    questions: (state, getters) => (relationshipArray) => {
      if (relationshipArray == null || state.objects.question == null) {
        return null
      } else {
        return mapRelationships(state, relationshipArray)
      }
    },

    question: (state, getters) => (relationshipHash) => {
      if (relationshipHash == null || state.objects.question == null) {
        return null
      } else {
        return mapRelationship(state, relationshipHash)
      }
    },

    answers: (state, getters) => (relationshipArray) => {
      if (relationshipArray == null || state.objects.answer == null) {
        return null
      } else {
        return mapRelationships(state, relationshipArray)
      }
    },

    attachmentsFromRelationships: (state, getters) => (relationshipsArray) => {
      if (relationshipsArray == null || state.objects.attachment == null) {
        return []
      } else {
        return mapRelationships(state, relationshipsArray)
      }
    },

    handle: (state, getters) => state.currentTokenMerchant?.handle,

    merchantID: (state, getters) => state.currentTokenMerchant?.id,

    info: (state, getters) => {
      if (!state.objects.user[getters.merchantID]) {
        state.objects.user[getters.merchantID] = parseStoredJSON(['merchant-info-', getters.merchantID].join(''))
      }
      return state.objects.user[getters.merchantID]
    },

    merchant: (state, getters) => {
      if (!state.currentTokenMerchant) return null
      return state.objects.user[state.currentTokenMerchant.id]
    },

    hasV2Checkout: (state, getters) => {
      if (isV2Override()) return true
      return hasCapability(getters.info, 'v2-checkout')
    },

    aiReadableStorefront: (state, getters) => hasCapability(getters.info, 'ai-readable-storefront'),

    menuBeforeCart: (state, getters) => hasCapability(getters.info, 'menu-before-cart'),

    imageAlt: (state, getters) => (description, fallback) =>
      getters.aiReadableStorefront && description ? description : fallback,

    storeAccessParams: (state) => (storeId) => {
      const token = state.storeAccessTokens[storeId]
      return token ? { store_access_token: token } : {}
    },

    hasCartModals: () => true,

    hasCartDrawer: () => true,

    submittableCartItems: (state, getters) => (categoryProductStorePairID) => {
      return getters.submittableCart?.filter((x) => x.category_product_store_pair_id == categoryProductStorePairID)
    },

    submittableCart: (state, getters) => {
      if (getters.newSubmittableCart) {
        return getters.newSubmittableCart
      } else {
        return getters.dynamicSubmittableCart
      }
    },

    newSubmittableCart: (state, getters) => {
      return state.objects.submittable_cart[getters.currentCart?.id]
    },

    dynamicSubmittableCart: (state, getters) => {
      // let cartID = getters.currentCart?.id
      let cartItems = mapRelationships(state, getters.currentCart?.relationships?.cart_items?.data)

      // id, quantity, frontend_key, category_product_store_pair_id, cart_item_variant_options_attributes: [:variant_option_id]
      let submittableCartItems = cartItems?.map((item) => ({
        type: item.attributes?.type,
        quantity: item.attributes?.quantity,
        frontend_key: item.attributes?.frontend_key,
        default_price: item.attributes?.default_price,
        membership_price: item.attributes?.membership_price,
        variants_price: item.attributes?.variants_price,
        default_price_plus_variants_price: item.attributes?.default_price_plus_variants_price,
        membership_price_plus_variants_price: item.attributes?.membership_price_plus_variants_price,
        net_price_plus_net_variants_price: item.attributes?.net_price_plus_net_variants_price,
        category_product_store_pair_id: item.attributes?.category_product_store_pair_id,
        product_id: item.attributes?.product_id,
        fulfillment_group_date: item.attributes?.fulfillment_group_date,
        nutritional_information_combined: item.attributes?.nutritional_information_combined,
        cart_item_variant_options_attributes: mapRelationships(
          state,
          item.relationships?.cart_item_variant_options?.data
        ).map((pair) => ({
          variant_option_id: pair.attributes?.variant_option_id?.toString(),
          variant_id: pair.attributes?.variant_id.toString(),
          quantity: pair.attributes?.quantity
        }))
      }))

      return submittableCartItems
    },

    categories: (state, getters) =>
      mapRelationships(
        state,
        getters.store(getters.currentBottle?.relationships?.store?.data)?.relationships?.categories?.data
      ).filter((x) => x.attributes.type !== 'UpsellCategory'),

    categoryProductStorePairs: (state, getters) => (relationshipsArray) => {
      if (relationshipsArray == null || state.objects.category_product_store_pair == null) {
        return []
      } else {
        return mapRelationships(state, relationshipsArray)
      }
    },

    category: (state, getters) => (relationshipHash) => {
      if (relationshipHash == null || state.objects.product == null) {
        return null
      } else {
        return mapRelationship(state, relationshipHash)
      }
    },

    products: (state, getters) => (relationshipsArray) => {
      if (relationshipsArray == null || state.objects.product == null) {
        return []
      } else {
        return mapRelationships(state, relationshipsArray)
      }
    },

    product: (state, getters) => (relationshipHash) => {
      if (relationshipHash == null || state.objects.product == null) {
        return null
      } else {
        return mapRelationship(state, relationshipHash)
      }
    },

    cartItems: (state, getters) => mapIncluded(state, getters.currentCart, 'cart_item'),

    attachments: (state) => (productId) => mapIncluded(state, state.objects.product[productId], 'attachment'),

    labels: (state) => (productId) => mapIncluded(state, state.objects.product[productId], 'label'),

    labelsFromRelationships: (state) => (relationshipsArray) => mapRelationships(state, relationshipsArray),

    pricingTiers: (state, getters) => {
      return Object.values(state.objects.pricing_tier)
    },

    // Every tier the page has been sent, whatever store or customer it came for. A picker
    // offers storeMembershipTiers or possibleMembershipTiers instead.
    membershipTiers: (state, getters) =>
      state.objects.membership_tier && sortMembershipTiers(Object.values(state.objects.membership_tier)),

    storeMembershipTiers: (state, getters) => checkoutMembershipTiers(state, getters.bottleId),

    possibleMembershipTiers: (state, getters) => accountMembershipTiers(state, getters.customer),

    distributionLists: (state, getters) => (relationshipsArray) => {
      if (relationshipsArray == null || state.objects.distribution_list == null) {
        return []
      } else {
        return mapRelationships(state, relationshipsArray)
      }
    },

    variants: (state, getters) => (productId) => mapIncluded(state, state.objects.product[productId], 'variant'),

    variantOptions: (state, getters) => (variantId) =>
      mapIncluded(state, state.objects.variant[variantId], 'variant_option'),

    variantOptionsWithPointer: (state, getters) => (relationshipArray) => {
      return mapRelationships(state, relationshipArray)
    },

    // pair: (state) => (productId, categoryId) => {
    //   const productPairs = state.objects.product[productId].relationships.category_product_store_pairs.data.map((x) =>
    //     Number(x.id)
    //   )

    //   const categoryPairs = state.objects.category[categoryId].relationships.category_product_store_pairs.data.map(
    //     (x) => Number(x.id)
    //   )

    //   const categoryPairsSet = new Set(categoryPairs)
    //   return productPairs.find((x) => categoryPairsSet.has(x))
    // },

    fulfillmentSlot: (state, getters) => {
      if (!getters.currentBottle?.relationships?.fulfillment_slot.data) return null
      return state.objects.fulfillment_slot[getters.currentBottle.relationships.fulfillment_slot.data.id]
    },

    fulfillmentSlotForBottle: (state, getters) => (bottle) => {
      if (!bottle || !getters.bottleWithID(bottle.id).relationships?.fulfillment_slot.data) return null
      return state.objects.fulfillment_slot[bottle.relationships.fulfillment_slot.data.id]
    },

    fulfillmentMethodForBottle: (state, getters) => (bottle) => {
      if (!getters.bottleWithID(bottle.id)?.relationships?.fulfillment_method.data) return null

      const fulfillmentMethodId = getters.bottleWithID(bottle.id).relationships.fulfillment_method.data.id
      return state.objects.fulfillment_method[fulfillmentMethodId]
    },
    fulfillmentSlotForFulfillmentMethod: (state, getters) => (fulfillmentMethod) => {
      const fulfillmentSlotId = fulfillmentMethod
      return state.objects.fulfillment_slot[fulfillmentSlotId]
    },

    fulfillmentSlots: (state, getters) => (relationshipsArray) => {
      if (relationshipsArray == null || state.objects.fulfillment_method == null) {
        return []
      } else {
        return mapRelationships(state, relationshipsArray)
      }
    },

    fulfillmentMethod: (state, getters) => {
      if (getters.fulfillmentSlot) {
        return state.objects.fulfillment_method[getters.fulfillmentSlot.relationships.fulfillment_method.data.id]
      } else {
        return null
      }
    },

    fulfillmentMethods: (state, getters) => (relationshipsArray) => {
      if (relationshipsArray == null || state.objects.fulfillment_method == null) {
        return []
      } else {
        return mapRelationships(state, relationshipsArray)
      }
    },

    deliveryAddresses: (state, getters) => (relationshipsArray) => {
      if (relationshipsArray == null || state.objects.delivery_address == null) {
        return []
      } else {
        return mapRelationships(state, relationshipsArray)
      }
    },

    deliveryAddress: (state, getters) => (deliveryAddressID) => {
      if (state.objects.delivery_address == null) {
        return null
      }
      return state.objects.delivery_address[deliveryAddressID]
    },

    stripePaymentMethods: (state, getters) => (relationshipsArray) => {
      if (relationshipsArray == null || state.objects.stripe_payment_method == null) {
        return []
      } else {
        return mapRelationships(state, relationshipsArray)
      }
    },

    stripePaymentMethod: (state, getters) => (stripePaymentMethodID) => {
      if (state.objects.stripe_payment_method == null) {
        return null
      }
      return state.objects.stripe_payment_method[stripePaymentMethodID]
    },

    pickupFulfillmentMethods: (state, getters) => (bottleId) => {
      return []
    },

    membershipTier: (state, getters) => {
      if (!getters.currentBottle?.relationships.membership_tier?.data) return null
      return state.objects.membership_tier?.[getters.currentBottle.relationships.membership_tier.data.id]
    },

    membershipTierFromRelationship: (state, getters) => (relationshipHash) => {
      if (relationshipHash == null || state.objects.membership_tier == null) {
        return null
      } else {
        return mapRelationship(state, relationshipHash)
      }
    },

    distributionList: (state, getters) => {
      // TODO(alicja): change to state.currentBottle.relationships.distribution_list.data.id
      // after https://github.com/bottle-labs/bottle/issues/455 is closed
      if (!getters.currentBottle?.attributes.distribution_list_id) return null
      return state.objects.distribution_list?.[getters.currentBottle.attributes.distribution_list_id]
    },

    distributionListFromRelationship: (state, getters) => (relationshipHash) => {
      if (relationshipHash == null || state.objects.distribution_list == null) {
        return null
      } else {
        return mapRelationship(state, relationshipHash)
      }
    },

    checkout: (state, getters) => {
      if (!getters.currentBottle?.relationships.checkout?.data) return null
      return state.objects.checkout[getters.currentBottle.relationships.checkout.data.id]
    },

    checkoutFromRelationship: (state, getters) => (relationshipHash) => {
      if (relationshipHash == null || state.objects.checkout == null) {
        return null
      } else {
        return mapRelationship(state, relationshipHash)
      }
    },

    bottlePayment: (state, getters) => {
      if (!getters.checkout?.relationships?.bottle_payment?.data) return null
      return state.objects.bottle_payment?.[getters.checkout?.relationships?.bottle_payment.data.id]
    },
    // BOT-1546: the Stripe invoice for an order placed on an invoice-only store.
    bottleInvoice: (state, getters) => {
      const ref = getters.currentBottle?.relationships?.bottle_invoice?.data
      if (!ref) return null
      return state.objects.bottle_invoice?.[ref.id] || null
    },

    bottlePaymentFromRelationship: (state, getters) => (relationshipHash) => {
      if (relationshipHash == null || state.objects.bottle_payment == null) {
        return null
      } else {
        return mapRelationship(state, relationshipHash)
      }
    },

    stripePaymentIntent: (state, getters) => {
      if (!getters.currentBottle?.relationships.stripe_payment_intent?.data) return null
      return state.objects.stripe_payment_intent[getters.currentBottle.relationships.stripe_payment_intent.data.id]
    },

    stripePaymentIntentFromRelationship: (state, getters) => (relationshipHash) => {
      if (relationshipHash == null || state.objects.stripe_payment_intent == null) {
        return null
      } else {
        return mapRelationship(state, relationshipHash)
      }
    }
  },
  mutations: {
    ADD_PENDING_ITEM(state, item) {
      state.pendingItems.push(item)
    },

    SET_CART_HANDOFF_SCROLL(state, scrollY) {
      state.cartHandoffScrollY = scrollY
    },

    SET_CART_DRAWER_LEFT_OPEN_FOR(state, bottleId) {
      state.cartDrawerLeftOpenFor = bottleId
    },

    SET_STORE_ACCESS_TOKEN(state, { storeId, token }) {
      state.storeAccessTokens = { ...state.storeAccessTokens, [storeId]: token }
    },

    UPDATE_PENDING_ITEM(state, { key, cartAttributes }) {
      const item = state.pendingItems.find((x) => x.cartAttributes.key === key)
      item.cartAttributes = { ...item.cartAttributes, ...cartAttributes }
    },

    SET_MAIN_CONTENT_LOADED(state, value) {
      state.mainContentLoaded = value
    },

    SET_FACEBOOK_PIXEL_INITIALIZED(state, value) {
      state.facebookPixelInitialized = value
    },

    RECEIVE_OBJECT(state, object) {
      function set(obj) {
        const type = obj.type
        if (!state.objects[type]) state.objects[type] = {}
        state.objects[type][obj.id] = obj
      }
      if (!object) return
      if (Array.isArray(object.data)) {
        object.data.forEach((x) => set(x))
      } else {
        set(object.data)
      }
      if (object.included) {
        object.included.forEach((x) => set(x))
      }
    },

    SET_BOTTLE_MEMBERSHIP_TIER_IDS(state, { bottleId, ids }) {
      state.bottleMembershipTierIds[bottleId] = ids
    },

    SET_PROMISE(state, { name, promise }) {
      state.promises.set(name, promise)
    },

    CLEAR_PROMISES(state) {
      state.promises.clear()
    },

    DELETE_PROMISE(state, promiseName) {
      state.promises.delete(promiseName)
    },

    CLEAR_STRIPE_PAYMENT_METHODS(state) {
      state.objects.stripe_payment_method = {}
    },

    NEW_CART_CONTROLLER(state) {
      state.cartController.abort()
      state.cartController = new AbortController()
    },

    SET_CART_SUPERSEDED(state, value) {
      state.cartSuperseded = value
    },

    CLEAR_SUBMITTABLE_CARTS(state) {
      state.objects.submittable_cart = {}
    },

    SET_SUBMITTABLE_CART(state, { key, submittableCart }) {
      state.objects.submittable_cart[key] = submittableCart
    },

    SET_CURRENT_TOKENS(state, jwtString) {
      const decodedJWT = safeJwtDecode(jwtString)
      if (jwtString && !decodedJWT) {
        // A corrupt/undecodable token used to throw here and blank the page.
        // Drop it so the app recovers as logged-out and fetches a fresh one.
        removeStorageItem('token')
      }
      state.currentTokenMerchant = decodedJWT?.merchant
      state.currentTokenCustomer = decodedJWT?.customer
      state.anonymousTokenSecret = decodedJWT?.anonymous_token
      state.conversationUserPairId = decodedJWT?.conversation_user_pair_id ?? null
      state.franchiseId = decodedJWT?.franchise_id
      state.decodedJWT = decodedJWT

      if (decodedJWT?.customer) {
        posthog.identify(decodedJWT.customer.phone)
        posthog.people.set({
          customer_id: decodedJWT.customer.id,
          customer_phone: decodedJWT.customer.phone,
          handle: decodedJWT.merchant.handle,
          merchant_id: decodedJWT.merchant.id
        })
      }
    },

    SET_DISMISSED_LOGGED_IN_FOR(state, loggedInFor) {
      state.dismissedLoggedInAsFor = loggedInFor
    },

    SET_SPLIT_DAY(state, splitDay) {
      state.splitDay = splitDay
    },

    SET_SELECTED_STORE_SLUG(state, storeSlug) {
      state.selectedStoreSlug = storeSlug
      try {
        if (storeSlug) {
          localStorage.setItem('selectedStoreSlug', storeSlug)
        } else {
          localStorage.removeItem('selectedStoreSlug')
        }
      } catch (e) {
        // ignore storage errors
      }
    }
  },
  actions: {
    // getters

    initializeCurrentTokens({ state, commit, getters }) {
      if (!state.currentTokenMerchant && getStorageItem('token')) {
        commit('SET_CURRENT_TOKENS', getStorageItem('token'))
      }
    },

    async getToken({ state, commit }, { handle, subdomain, store_slug, bottle_id }) {
      const name =
        'token-' +
        (localStorage.getItem('token') ? localStorage.getItem('token').toString() : '') +
        (store_slug ? `-${store_slug}` : '') +
        (bottle_id ? `-b${bottle_id}` : '')

      if (state.promises.has(name)) return state.promises.get(name)
      if (
        getStorageItem('token') &&
        safeJwtDecode(getStorageItem('token'), { clearKey: 'token' })?.merchant &&
        !store_slug &&
        !bottle_id
      ) {
        commit('SET_CURRENT_TOKENS', localStorage.getItem('token'))
        commit('SET_PROMISE', {
          name: name,
          promise: Promise.resolve(localStorage.getItem('token'))
        })
        return state.promises.get(name)
      }

      commit('SET_PROMISE', {
        name: name,
        promise: fetch(process.env.VUE_APP_API_BASE_URL + '/merchant/tokens', {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          method: 'POST',
          body: JSON.stringify({
            handle,
            subdomain,
            store_slug,
            bottle_id
          })
        })
          .then((res) => res.json())
          .then((data) => {
            localStorage.setItem('token', data.token)
            commit('SET_CURRENT_TOKENS', localStorage.getItem('token'))
            return data.token
          })
      })

      return state.promises.get(name)
    },

    async getDrops({ state, commit, getters }, storeId) {
      const name = 'drops-' + storeId.toString() + Math.random().toString(36).slice(2)
      if (state.promises.has(name)) return state.promises.get(name)
      commit('SET_PROMISE', {
        name: name,
        promise: merchantFetch('stores/' + storeId + '/drops', {
          method: 'GET',
          includes: ['cart']
        }).then((res) => {
          commit('RECEIVE_OBJECT', res)
          return res.data
        })
      })

      return state.promises.get(name)
    },

    async getStoreBottles({ state, commit, getters }, storeId) {
      const name = 'bottleId-' + storeId.toString() + Math.random().toString(36).slice(2)
      if (state.promises.has(name)) return state.promises.get(name)

      commit('SET_PROMISE', {
        name: name,
        promise: merchantFetch('stores/' + storeId + '/bottles', {
          method: 'GET',
          params: getters.storeAccessParams(storeId),
          includes: [
            'cart',
            'cart.cart_items',
            'cart.cart_items.product',
            'cart.cart_items.product.attachments',
            'fulfillment_slot',
            'fulfillment_slot.fulfillment_method'
          ]
        }).then((res) => {
          commit('RECEIVE_OBJECT', res)
          return res.data
        })
      })

      return state.promises.get(name)
    },

    async getNewBottleId({ state, commit, getters }, storeId) {
      const name = 'newBottleId-' + storeId.toString() + Math.random().toString(36).slice(2)
      if (state.promises.has(name)) return state.promises.get(name)

      commit('SET_PROMISE', {
        name: name,
        promise: merchantFetch('stores/' + storeId + '/bottles', {
          params: { generate_new_bottle: true, ...getPersistedUtmParams(), ...getters.storeAccessParams(storeId) }
        }).then((res) => {
          commit('RECEIVE_OBJECT', res)
          return res.data
        })
      })

      return state.promises.get(name)
    },

    async unlockStore({ commit }, { storeId, password }) {
      const res = await merchantFetch('stores/' + storeId + '/unlocks', {
        method: 'POST',
        body: JSON.stringify({ password })
      })
      commit('SET_STORE_ACCESS_TOKEN', { storeId, token: res.store_access_token })
    },

    // Drop the persisted token so the next request re-mints a fresh one for the
    // current store. Used when the token turns out to be stale/broken (e.g. it
    // was issued for another merchant or environment). See getBottle / BOT-973.
    resetAnonymousToken({ state, commit }) {
      removeStorageItem('token')
      // Invalidate any memoised getToken promise so the next call re-mints
      // instead of handing back the broken token.
      for (const name of [...state.promises.keys()]) {
        if (name.startsWith('token-')) commit('DELETE_PROMISE', name)
      }
      // Keep the decoded token state until the fresh token overwrites it
      // (getToken commits SET_CURRENT_TOKENS). Blanking it here nulls
      // `getters.info`, SelectionRouter unmounts the storefront, the re-mint
      // remounts it, `mounted` fetches the bottle again and a bottle the
      // visitor can never access (BOT-1643) spins forever.
    },

    async getBottle({ state, commit, getters, dispatch }, { includes, temporaryActualUltramarineEnable = false } = {}) {
      const promiseKey = 'bottle-' + getters.bottleId + JSON.stringify(includes)
      // if (state.promises.has(promiseKey)) return state.promises.get(promiseKey)
      const fetchBottle = () =>
        merchantFetch('bottles/' + getters.bottleId, {
          includes,
          context: { bottle: getters.bottleId },
          temporaryActualUltramarineEnable
        }).then((res) => {
          commit('RECEIVE_OBJECT', res)
          return res.data.attributes.id
        })

      commit('SET_PROMISE', {
        name: promiseKey,
        promise: fetchBottle().catch((error) => {
          // A 404 means the bottle id didn't resolve for the merchant we
          // authenticated as. The usual cause is a stale/broken anonymous token
          // persisted from another merchant or environment, which getToken
          // happily replays. Drop it, re-mint a fresh token for the current
          // store, and retry once so checkout recovers instead of bricking on a
          // blank screen. Any other error (or a second 404) propagates. BOT-973.
          if (String(error.message) !== '404') throw error
          return Promise.resolve(dispatch('resetAnonymousToken')).then(fetchBottle)
        })
      })

      return state.promises.get(promiseKey)
    },

    async sendUtmAttribution({ state, commit, getters }) {
      const utmParams = getPersistedUtmParams()
      if (!Object.keys(utmParams).length) return
      const bottle = getters.currentBottle
      if (bottle?.attributes?.utm_source) return

      const res = await merchantFetch('bottles/' + getters.bottleId, {
        method: 'PUT',
        body: JSON.stringify({ bottle: utmParams })
      })
      commit('RECEIVE_OBJECT', res)
    },

    async getBottleCheckout({ state, commit, getters }, { includeCheckout = false } = {}) {
      if (state.promises.has('bottleCheckout')) return state.promises.get('bottleCheckout')
      // The serializer only generates a checkout when 'checkout' is in the
      // includes list (serializer_includes_methods#generate_checkout?) — a
      // bare generate_checkout query param is ignored by the controller. The
      // drawer opts in via includeCheckout; other callers keep the legacy
      // request shape.
      const options = includeCheckout ? { includes: ['checkout'] } : { params: { generate_checkout: true } }
      commit('SET_PROMISE', {
        name: 'bottleCheckout',
        promise: merchantFetch('bottles/' + getters.bottleId, options).then((res) => {
          commit('RECEIVE_OBJECT', res)
          return res.data.attributes.id
        })
      })

      return state.promises.get('bottleCheckout')
    },

    async getStores({ state, commit, dispatch, getters }) {
      const name = 'stores'
      if (state.promises.has(name)) return state.promises.get(name)

      commit('SET_PROMISE', {
        name,
        promise: merchantFetch('stores', { includes: ['attachments'] }).then((res) => {
          commit('RECEIVE_OBJECT', res)
        })
      })

      return state.promises.get(name)
    },

    async showStore({ state, commit, getters, dispatch }, { storeId, storeSlug }) {
      const name = 'store-' + storeId.toString() + Math.random().toString(36).slice(2)
      if (state.promises.has(name)) return state.promises.get(name)

      // Use passed storeSlug or fall back to state
      const slug = storeSlug || state.selectedStoreSlug

      commit('SET_PROMISE', {
        name: name,
        promise: merchantFetch('stores/' + storeId, { params: { included: 'none' } })
          .then((res) => {
            commit('RECEIVE_OBJECT', res)
            return res.data
          })
          .catch(async () => {
            localStorage.removeItem('token')
            localStorage.removeItem('selectedStoreSlug')
            localStorage.removeItem(['merchant-info-', getters.merchantID].join(''))
            commit('CLEAR_PROMISES')

            await dispatch('getToken', {
              subdomain: window.location.host,
              store_slug: slug
            })
            commit('SET_SELECTED_STORE_SLUG', slug)

            const retryRes = await merchantFetch('stores/' + storeId, { params: { included: 'none' } })
            commit('RECEIVE_OBJECT', retryRes)
            return retryRes.data
          })
      })

      return state.promises.get(name)
    },

    async getPages({ state, commit, dispatch, getters }) {
      const name = 'pages'
      if (state.promises.has(name)) return state.promises.get(name)
      if (!getters.merchantID) return

      commit('SET_PROMISE', {
        name,
        promise: merchantFetch('pages', {
          method: 'GET',
          includes: [
            'page_modules',
            'page_modules.content_elements',
            'page_modules.content_elements.attachments',
            'page_modules.form'
          ]
        }).then((res) => {
          commit('RECEIVE_OBJECT', res)
        })
      })

      return state.promises.get(name)
    },

    async getStore({ state, commit, dispatch, getters }) {
      const name = 'bottles/' + getters.bottleId + '/stores'
      if (state.promises.has(name)) return state.promises.get(name)

      commit('SET_PROMISE', {
        name,
        promise: merchantFetch('bottles/' + getters.bottleId + '/stores', {
          includes: [
            'categories',
            'pricing_tiers',
            'membership_tiers',
            'membership_tiers.cart_subscription_settings',
            'attachments',
            'possible_packages',
            'questions'
          ],
          context: { bottle: getters.bottleId },
          temporaryActualUltramarineEnable: true
        }).then((res) => {
          commit('RECEIVE_OBJECT', res)
        })
      })

      return state.promises.get(name)
    },

    async getStoreDetailsForBottle({ state, commit, dispatch, getters }) {
      const membershipTierId = getters.currentBottle?.relationships?.membership_tier?.data?.id
      const name = 'bottles/' + getters.bottleId + '/store/' + membershipTierId

      if (state.promises.has(name)) return state.promises.get(name)

      commit('SET_PROMISE', {
        name,
        promise: merchantFetch('bottles/' + getters.bottleId + '/store', {
          includes: [
            'product',
            'product.attachments',
            'product.labels',
            'product.variants',
            'product.variants.variant_options',
            'product.nutritional_information'
          ],
          context: { bottle: getters.bottleId },
          temporaryActualUltramarineEnable: true
        }).then((res) => {
          commit('RECEIVE_OBJECT', res)
        })
      })
      return state.promises.get(name)
    },

    async getInfo({ state, commit, getters, dispatch }) {
      if (state.promises.has('info')) return state.promises.get('info')

      const loadInfo = async () => {
        // A direct bottle link opened with a blank/missing token in storage has
        // no currentTokenMerchant, so merchantID is undefined. The store page
        // bootstraps a token via showStore -> merchantFetch, but this route only
        // dispatches getInfo, so without minting one here nothing ever would:
        // getInfo used to bail, leaving the page on a perpetual skeleton with no
        // API call and no error. Mint an anonymous token for this merchant
        // (resolved by subdomain, no existing token required) so the merchant
        // hydrates and we can proceed. BOT-987.
        //
        // On a franchise root domain the subdomain alone resolves NO merchant,
        // so also send any store slug / bottle id the URL carries — the backend
        // uses them to pick the franchise location. BOT-1295.
        if (!getters.merchantID) {
          await dispatch('getToken', {
            subdomain: window.location.host,
            ...franchiseStoreContextFromLocation()
          })
        }
        if (!getters.merchantID) {
          // Still merchant-less (franchise root, no store chosen yet). Drop the
          // memoised promise so a later dispatch — after the store-slug flow
          // mints a merchant-scoped token — retries instead of replaying this
          // dead result forever. BOT-1295.
          commit('DELETE_PROMISE', 'info')
          return
        }

        const res = await merchantFetch('info')
        setStorageItem(['merchant-info-', res.data.id].join(''), JSON.stringify(res.data))
        commit('RECEIVE_OBJECT', res)
      }

      commit('SET_PROMISE', { name: 'info', promise: loadInfo() })

      return state.promises.get('info')
    },

    async getCustomerInfo({ state, commit, dispatch, getters }, includes) {
      try {
        const res = await merchantFetch('customer', { includes })
        commit('RECEIVE_OBJECT', res)
      } catch (e) {
        // /merchant/customer returns 400 when the JWT has no customer context
        // (anonymous visitors). Expected on first load — don't surface as an error.
      }
    },

    startResellerImpersonation({ commit }, { token }) {
      if (!localStorage.getItem('reseller_return_token')) {
        localStorage.setItem('reseller_return_token', localStorage.getItem('token') || '')
      }
      localStorage.setItem('token', token)
      commit('SET_CURRENT_TOKENS', token)
    },

    exitResellerImpersonation({ commit }) {
      const originalToken = localStorage.getItem('reseller_return_token')
      localStorage.removeItem('reseller_return_token')
      if (!originalToken) return null
      localStorage.setItem('token', originalToken)
      commit('SET_CURRENT_TOKENS', originalToken)
      return originalToken
    },

    async getIssuedGiftCard({ state, commit, dispatch, getters }, code) {
      const res = await merchantFetch('issued_gift_cards/' + code, {
        method: 'GET'
      })
      if (!res?.errors) commit('RECEIVE_OBJECT', res)
      return res
    },

    async getBottleIssuedGiftCards({ commit }, bottleId) {
      const res = await merchantFetch('issued_gift_cards', {
        method: 'GET',
        params: { bottle_id: bottleId }
      })
      if (!res?.errors) commit('RECEIVE_OBJECT', res)
      return res?.data ?? []
    },

    async resendGiftCard({ state }, { code, email, message }) {
      const res = await merchantFetch('issued_gift_cards/' + code + '/resend_email', {
        method: 'POST',
        body: JSON.stringify({ email, message })
      })
      return res
    },

    async addDiscountCode({ state, commit, dispatch, getters }, discountCodeName) {
      const res = await merchantFetch('bottles/' + getters.bottleId + '/discount_codes', {
        method: 'POST',
        params: { code: discountCodeName }
      })
      if (!res?.errors) {
        commit('RECEIVE_OBJECT', res)

        const bottleFromResponse =
          res.data?.type === 'bottle' ? res.data : res.included?.find((item) => item.type === 'bottle')
        const isGiftCard =
          bottleFromResponse?.attributes?.gift_card_applied === true ||
          getters.currentBottle?.attributes?.gift_card_applied === true

        const discountCodeType = isGiftCard ? 'GiftCard' : res.data?.attributes?.code_type

        if (discountCodeType) {
          const context = { getters: getters, state: state }
          const trackingProperties = {
            ...getCommonTrackingProperties(context),
            discount_code_type: discountCodeType
          }
          posthog.capture('applied_discount_code', trackingProperties)
        }
      }
      return res
    },

    async removeAppliedDiscountCode({ state, commit, dispatch, getters }, appliedDiscountCodeID) {
      const res = await merchantFetch(
        'bottles/' + getters.bottleId + '/applied_discount_codes/' + appliedDiscountCodeID,
        {
          method: 'DELETE'
        }
      )
      commit('RECEIVE_OBJECT', res)
    },

    async markBottleAsPaid({ state, commit, dispatch, getters }) {
      const res = await merchantFetch('bottles/' + getters.bottleId + '/pay', {
        method: 'POST'
      })
      if (!res?.errors) commit('RECEIVE_OBJECT', res)
      return res
    },

    // BOT-1546: invoice-only stores place the order without payment.
    async placeInvoiceOrder({ commit, getters }) {
      const res = await merchantFetch('bottles/' + getters.bottleId + '/invoices', {
        method: 'POST',
        params: { included: 'bottle,bottle.bottle_invoice,checkout' }
      })
      if (!res?.errors) commit('RECEIVE_OBJECT', res)
      return res
    },

    // Accepts a bottle id, or { bottleID, stripePaymentMethodId }. Passing the
    // Stripe payment method the customer is about to pay with lets the server
    // price the checkout for that card before it is charged (no processing fee
    // on debit/prepaid cards, BOT-1446).
    async bottleValidation({ state, commit, getters }, payload) {
      const { bottleID, stripePaymentMethodId } =
        payload && typeof payload === 'object' ? payload : { bottleID: payload, stripePaymentMethodId: null }
      const name = 'bottleValidation-' + bottleID.toString() + Math.random().toString(36).slice(2)
      if (state.promises.has(name)) return state.promises.get(name)

      const params = stripePaymentMethodId ? { stripe_payment_method_id: stripePaymentMethodId } : {}
      commit('SET_PROMISE', {
        name: name,
        promise: merchantFetch('bottles/' + bottleID + '/validation', {
          method: 'GET',
          params
        }).then((res) => {
          commit('RECEIVE_OBJECT', res.objects)
          return res
        })
      })

      return state.promises.get(name)
    },

    async answerQuestion({ state, commit, dispatch, getters }, { questionID, payload }) {
      const res = await merchantFetch('bottles/' + getters.bottleId + '/questions/' + questionID + '/answers', {
        method: 'POST',
        body: JSON.stringify(payload),
        includes: [
          'question',
          'bottle',
          'bottle.questions',
          'bottle.checkout_questions',
          'bottle.selection_questions',
          'bottle.cart',
          'bottle.cart.cart_items',
          'bottle.cart.cart_items.cart_item_variant_options',
          'bottle.children_bottles',
          'bottle.fulfillment_slot',
          'bottle.children_bottles.fulfillment_slot'
        ]
      })
      if (!res?.errors) {
        commit('RECEIVE_OBJECT', res)
        invalidateUpsellsCache(state, commit)
      }
      return res
    },

    async updateCustomerInfo({ state, commit }, payload) {
      const name = 'update-info-' + localStorage.getItem('token').toString() + Math.random().toString(36).slice(2)
      // if (state.promises.has(name)) return state.promises.get(name)
      commit('SET_PROMISE', {
        name: name,
        promise: merchantFetch('customer/profile', {
          method: 'POST',
          body: JSON.stringify(payload),
          includes: [
            'current_membership_tier_selection',
            'current_membership_tier_selection.cart_subscriptions',
            'current_membership_tier_selection.membership_tier',
            'current_membership_tier_selection.membership_tier.cart_subscription_settings',
            'current_cart_subscription',
            'current_cart_subscription.possible_packages',
            'current_cart_subscription.package'
          ]
        }).then((res) => {
          commit('RECEIVE_OBJECT', res)
        })
      })

      return state.promises.get(name)
    },

    async getMembershipTiers({ state, commit, getters }) {
      const bottleId = getters.bottleId
      const name = 'tiers/' + bottleId
      if (state.promises.has(name)) return state.promises.get(name)

      commit('SET_PROMISE', {
        name,
        promise: merchantFetch('bottles/' + bottleId + '/membership_tiers', {
          includes: [
            'distribution_lists',
            'cart_subscription_settings',
            'membership_pass',
            'membership_pass.membership_pass_rewards',
            'meal_plan',
            'autocharge_summary'
          ]
        }).then((res) => {
          commit('RECEIVE_OBJECT', res)
          commit('SET_BOTTLE_MEMBERSHIP_TIER_IDS', { bottleId, ids: (res?.data || []).map((tier) => tier.id) })
        })
      })

      return state.promises.get(name)
    },

    async getUpsells({ state, commit, getters }) {
      const name = 'upsells/' + getters.bottleId + '/' + getters.currentCart?.id
      if (state.promises.has(name)) return state.promises.get(name)

      // TODO(alicja): terrible but it'll do for now
      const timer = (ms) => new Promise((res) => setTimeout(res, ms))
      while (!getters.currentCart?.id) await timer(50)

      commit('SET_PROMISE', {
        name,
        promise: merchantFetch('bottles/' + getters.bottleId + '/carts/' + getters.currentCart?.id + '/upsells', {
          method: 'GET',
          includes: ['category']
        })
      })

      return state.promises.get(name)
    },

    async getUpsellsWithProducts({ state, commit, getters }) {
      // Use a unique name that changes with cart state to allow re-fetching
      const name = 'upsells-with-products/' + getters.bottleId + '/' + getters.currentCart?.id + '/' + getters.cartSize

      // Don't cache this - we want fresh data when cart changes
      const timer = (ms) => new Promise((res) => setTimeout(res, ms))
      while (!getters.currentCart?.id) await timer(50)

      const response = await merchantFetch(
        'bottles/' + getters.bottleId + '/carts/' + getters.currentCart?.id + '/upsells',
        {
          method: 'GET',
          includes: [
            'category',
            'product',
            'product.attachments',
            'product.labels',
            'product.variants',
            'product.variants.variant_options',
            'product.nutritional_information'
          ]
        }
      )

      // Store the included objects in the state
      if (response) {
        commit('RECEIVE_OBJECT', response)
      }

      return response
    },

    // async getDistributionLists({ state, commit, getters }) {
    //   const name = 'lists/' + getters.bottleId
    //   if (state.promises.has(name)) return state.promises.get(name)

    //   commit('SET_PROMISE', {
    //     name,
    //     promise: merchantFetch('bottles/' + getters.bottleId + '/distribution_lists').then((res) => {
    //       commit('RECEIVE_OBJECT', res)
    //     })
    //   })

    //   return state.promises.get(name)
    // },

    // setters

    async confirmCart({ state, commit, getters, dispatch }) {
      const res = await merchantFetch(
        'bottles/' + getters.bottleId + '/carts/' + getters.currentCart?.id + '/confirmations',
        {
          method: 'POST'
        }
      )
      commit('RECEIVE_OBJECT', res)
    },

    async skipBottle({ state, commit, getters, dispatch }, bottleID) {
      // Use the provided bottleID or fall back to the currentBottle's id.
      const id = bottleID || getters.bottleId
      const res = await merchantFetch('bottles/' + id + '/skips', {
        method: 'POST',
        body: JSON.stringify({})
      })
      commit('RECEIVE_OBJECT', res)
    },

    async unskipBottle({ state, commit, getters, dispatch }, bottleID) {
      const id = bottleID || getters.bottleId
      const res = await merchantFetch('bottles/' + id + '/unskips', {
        method: 'POST',
        body: JSON.stringify({})
      })
      commit('RECEIVE_OBJECT', res)
    },

    async pauseMealPlan({ commit }) {
      const res = await merchantFetch('customer/meal_plan_pauses', { method: 'POST', body: JSON.stringify({}) })
      if (!res?.errors) commit('RECEIVE_OBJECT', res)
      return res
    },

    async cancelMealPlan({ commit }) {
      const res = await merchantFetch('customer/meal_plan_cancellations', { method: 'POST', body: JSON.stringify({}) })
      if (!res?.errors) commit('RECEIVE_OBJECT', res)
      return res
    },
    async unpauseMealPlan({ commit }, subscriptionId) {
      const res = await merchantFetch('customer/meal_plan_pauses/' + subscriptionId, { method: 'DELETE' })
      if (!res?.errors) commit('RECEIVE_OBJECT', res)
      return res
    },

    async setNewDeliveryAddress(
      { state, commit, getters, dispatch },
      { address1, address2, city, addressState, zip, notes, latitude, longitude, bottle, replacingAddressId, isDefault }
    ) {
      let bottleId = getters.currentBottle?.id
      if (bottle) {
        bottleId = bottle.id
      }
      const res = await merchantFetch('bottles/' + bottleId + '/delivery_addresses', {
        method: 'POST',
        body: JSON.stringify({
          delivery_address: {
            address1: address1,
            address2: address2,
            city: city,
            state: addressState,
            zip: zip,
            notes: notes,
            latitude: latitude,
            longitude: longitude,
            is_default: isDefault === true,
            replacing_address_id: replacingAddressId
          }
        }),
        includes: [
          'checkout',
          'fulfillment_slot',
          'fulfillment_method',
          'delivery_address.eligible_fulfillment_methods',
          'fulfillment_slot.fulfillment_method',
          'possible_delivery_addresses',
          'possible_fulfillment_slot_days',
          'possible_fulfillment_slot_times',
          'possible_fulfillment_methods',
          'possible_fulfillment_methods.delivery_address',
          'possible_fulfillment_methods.fulfillment_slots_detail'
        ]
      })
      commit('RECEIVE_OBJECT', res)
    },

    async setDeliveryAddressID({ state, commit, getters, dispatch }, data) {
      const res = await merchantFetch('bottles/' + data['bottle'].id, {
        method: 'PUT',
        body: JSON.stringify({
          bottle: {
            delivery_address_id: data['deliveryAddressID']
          }
        }),
        includes: [
          'possible_stripe_payment_methods',
          'possible_fulfillment_methods',
          'possible_fulfillment_slot_days',
          'possible_fulfillment_slot_times',
          'possible_delivery_addresses',
          'possible_fulfillment_methods.delivery_address',
          'possible_fulfillment_methods.fulfillment_slots_detail',
          'checkout',
          'fulfillment_slot',
          'fulfillment_method',
          'cart',
          'cart.cart_items',
          'parent_bottle.children_bottles',
          'parent_bottle.children_bottles.checkout'
        ]
      })

      trackFulfillmentOptionSelected(res, getters, state)
      commit('RECEIVE_OBJECT', res)
    },

    async removeDeliveryAddress({ state, commit, getters, dispatch }, data) {
      const res = await merchantFetch(`bottles/${data.bottleId}/delivery_addresses/${data.deliveryAddressID}`, {
        method: 'DELETE',
        includes: [
          'bottle',
          'bottle.checkout',
          'bottle.parent_bottle.children_bottles',
          'bottle.parent_bottle.children_bottles.checkout'
        ]
      })

      commit('RECEIVE_OBJECT', res)
    },

    async selectFulfillmentMethod({ commit, getters }, data) {
      const res = await merchantFetch('bottles/' + data['bottle'].id + '/fulfillment_methods', {
        method: 'POST',
        body: JSON.stringify({
          fulfillment_method: { id: data['methodId'] }
        }),
        includes: [
          'possible_stripe_payment_methods',
          'possible_fulfillment_methods',
          'possible_fulfillment_slot_days',
          'possible_fulfillment_slot_times',
          'possible_delivery_addresses',
          'possible_fulfillment_methods.delivery_address',
          'possible_fulfillment_methods.fulfillment_slots_detail',
          'checkout',
          'fulfillment_slot',
          'fulfillment_method',
          'cart',
          'cart.cart_items',
          'parent_bottle.children_bottles',
          'parent_bottle.children_bottles.checkout'
        ]
      })
      commit('RECEIVE_OBJECT', res)
    },

    async selectFulfillmentSlot({ commit, getters, state }, slotAndBottleId) {
      const res = await merchantFetch('bottles/' + slotAndBottleId['bottleId'], {
        method: 'PUT',
        body: JSON.stringify({
          bottle: { fulfillment_slot_id: slotAndBottleId['newSlotId'] }
        }),
        includes: [
          'possible_stripe_payment_methods',
          'possible_fulfillment_methods',
          'possible_fulfillment_slot_days',
          'possible_fulfillment_slot_times',
          'possible_delivery_addresses',
          'possible_fulfillment_methods.delivery_address',
          'possible_fulfillment_methods.fulfillment_slots_detail',
          'checkout',
          'fulfillment_slot',
          'fulfillment_method',
          'cart',
          'cart.cart_items',
          'parent_bottle.children_bottles',
          'parent_bottle.children_bottles.checkout'
        ]
      })

      trackFulfillmentOptionSelected(res, getters, state)
      commit('RECEIVE_OBJECT', res)
      invalidateUpsellsCache(state, commit)
    },

    async setPackageID({ commit, getters, state }, packageID) {
      const res = await merchantFetch('bottles/' + getters.bottleId, {
        method: 'PUT',
        body: JSON.stringify({
          bottle: { package_id: packageID }
        }),
        includes: [
          'cart',
          'cart.cart_items',
          'package',
          // Without this the package arrives with pointers to metadata that is not in the payload,
          // so its preferred options cannot be resolved and the modal opens blank.
          'package.variant_preference_metadata',
          'cart.cart_items.cart_item_variant_options',
          'cart.cart_items.variants',
          'cart.cart_items.variants.variant_options'
        ]
      })
      commit('RECEIVE_OBJECT', res)
      invalidateUpsellsCache(state, commit)
    },

    async setMembershipTier({ commit, getters }, membershipTierId) {
      const res = await merchantFetch('bottles/' + getters.bottleId, {
        method: 'PUT',
        body: JSON.stringify({
          bottle: { membership_tier_id: membershipTierId }
        }),
        includes: [
          'possible_stripe_payment_methods',
          'possible_fulfillment_methods',
          'possible_fulfillment_slot_days',
          'possible_fulfillment_slot_times',
          'possible_delivery_addresses',
          'possible_fulfillment_methods.delivery_address',
          'possible_fulfillment_methods.fulfillment_slots_detail',
          'checkout',
          'fulfillment_slot',
          'fulfillment_method',
          'cart',
          'cart.cart_items',
          'cart.cart_items.product',
          'children_bottles.cart.cart_items',
          // A tier change can add or remove packages and clear the one the bottle held.
          'store',
          'store.possible_packages',
          'package'
        ]
      })
      commit('RECEIVE_OBJECT', res)
    },

    async setDistributionList({ commit, getters }, distributionListId) {
      const res = await merchantFetch('bottles/' + getters.bottleId, {
        method: 'PUT',
        body: JSON.stringify({
          bottle: { distribution_list_id: distributionListId }
        }),
        includes: [
          'possible_stripe_payment_methods',
          'possible_fulfillment_methods',
          'possible_fulfillment_slot_days',
          'possible_fulfillment_slot_times',
          'possible_delivery_addresses',
          'possible_fulfillment_methods.delivery_address',
          'possible_fulfillment_methods.fulfillment_slots_detail',
          'checkout',
          'fulfillment_slot',
          'fulfillment_method',
          'cart',
          'cart.cart_items',
          'cart.cart_items.product'
        ]
      })
      commit('RECEIVE_OBJECT', res)
    },

    async setIntervalCartSubscriptionSetting({ commit, getters }, intervalSettingId) {
      const res = await merchantFetch('bottles/' + getters.bottleId, {
        method: 'PUT',
        body: JSON.stringify({
          bottle: { interval_cart_subscription_setting_id: intervalSettingId }
        }),
        includes: [
          'possible_stripe_payment_methods',
          'possible_fulfillment_methods',
          'possible_fulfillment_slot_days',
          'possible_fulfillment_slot_times',
          'possible_delivery_addresses',
          'possible_fulfillment_methods.delivery_address',
          'possible_fulfillment_methods.fulfillment_slots_detail',
          'checkout',
          'fulfillment_slot',
          'fulfillment_method',
          'cart',
          'cart.cart_items',
          'cart.cart_items.product'
        ]
      })
      commit('RECEIVE_OBJECT', res)
    },

    async setAutochargeCartSubscriptionSetting({ commit, getters }, autochargeSettingId) {
      const res = await merchantFetch('bottles/' + getters.bottleId, {
        method: 'PUT',
        body: JSON.stringify({
          bottle: { autocharge_cart_subscription_setting_id: autochargeSettingId }
        }),
        includes: [
          'possible_stripe_payment_methods',
          'possible_fulfillment_methods',
          'possible_fulfillment_slot_days',
          'possible_fulfillment_slot_times',
          'possible_delivery_addresses',
          'possible_fulfillment_methods.delivery_address',
          'possible_fulfillment_methods.fulfillment_slots_detail',
          'checkout',
          'fulfillment_slot',
          'fulfillment_method',
          'cart',
          'cart.cart_items',
          'cart.cart_items.product'
        ]
      })
      commit('RECEIVE_OBJECT', res)
    },

    async setTipAmount({ commit, getters }, tipAmountId) {
      const res = await merchantFetch('bottles/' + getters.bottleId, {
        method: 'PUT',
        body: JSON.stringify({
          bottle: { tip_amount_id: tipAmountId }
        }),
        includes: ['checkout']
      })
      commit('RECEIVE_OBJECT', res)
    },

    async setCustomTipAmount({ commit, getters }, tipAmountCents) {
      const res = await merchantFetch('bottles/' + getters.bottleId, {
        method: 'PUT',
        body: JSON.stringify({
          bottle: { tip_amount_attributes: { type: 'CustomTipAmount', tip_amount_cents: tipAmountCents } }
        }),
        includes: ['checkout']
      })
      commit('RECEIVE_OBJECT', res)
    },

    async updateSubmittableCart({ state, commit, getters, dispatch }, { submittableCartItem, forceCheckout = false }) {
      let submittableCart = getters.submittableCart
      if (submittableCartItem.quantity == 0) {
        submittableCart = submittableCart.filter((item) => !(item.frontend_key == submittableCartItem.frontend_key))
      } else if (submittableCart.filter((item) => item.frontend_key == submittableCartItem.frontend_key).length > 0) {
        submittableCart.forEach(function (item, i) {
          if (item.frontend_key == submittableCartItem.frontend_key) submittableCart[i] = submittableCartItem
        })
      } else {
        submittableCart.push(submittableCartItem)
      }
      commit('SET_SUBMITTABLE_CART', {
        key: getters.currentCart?.id,
        submittableCart: submittableCart
      })
      const isUpsellChange = submittableCartItem.frontend_key?.includes('-upsell')
      // With the cart drawer active, every grid add must refresh the checkout
      // too — the drawer's totals footer reads it live on the selection screen.
      dispatch('submitNewCart', { forceCheckout: forceCheckout || isUpsellChange || getters.hasCartDrawer })
    },

    async submitNewCart({ state, commit, getters, dispatch }, { forceCheckout = false } = {}) {
      commit('NEW_CART_CONTROLLER')
      try {
        state.activelySubmittingCart = true

        const params = {}
        if (forceCheckout) {
          params.force_checkout = true
        }

        const res = await merchantFetch('bottles/' + getters.bottleId + '/carts', {
          signal: state.cartController.signal,
          method: 'POST',
          temporaryActualUltramarineEnable: true,
          params,
          includes: [
            'cart.bottle.fulfillment_slot',
            'cart.membership_tier',
            'cart.pricing_tier',
            'cart.next_pricing_tier',
            'cart.cart_items',
            'cart.cart_items.cart_item_variant_options',
            'category_product_store_pairs',
            'checkout'
          ],
          context: { bottle: getters.bottleId },
          body: JSON.stringify({
            cart: {
              cart_items_attributes: getters.submittableCart
            },
            base_cart_id: getters.currentCart?.id
          })
        })
        state.activelySubmittingCart = false

        commit('RECEIVE_OBJECT', res)
        commit('SET_CART_SUPERSEDED', false)
      } catch (e) {
        if (e.name === 'AbortError') {
          // aborted by a newer cart, which owns the flag now - leaving it on is correct
        } else if (e.status === 409) {
          // The lines were built from a cart the bottle no longer uses (SUPPORT-2992): this page's
          // copy is out of date, so show the cart as it is now instead of writing over it.
          state.activelySubmittingCart = false
          await dispatch('reloadSupersededCart')
        } else {
          // Nothing downstream clears this, and since BOT-1108 the modal's single "Add to cart" is
          // the only thing that writes the cart - so a failed POST used to leave the basket footer
          // spinning with no control left to retry from.
          state.activelySubmittingCart = false
          throw e
        }
      }
    },

    // SUPPORT-2992: the server refused the cart because this page built it from a cart the bottle
    // no longer uses. Drop the page's copy and read the cart back as it is now.
    async reloadSupersededCart({ commit, dispatch }) {
      commit('CLEAR_SUBMITTABLE_CARTS')
      commit('SET_CART_SUPERSEDED', true)
      await dispatch('getBottle', { includes: CHECKOUT_CART_INCLUDES })
    },

    // A checkout page coming back into view after a while re-reads its cart, so a tab left open
    // shows what was added from elsewhere in the meantime (SUPPORT-2992).
    async refreshCheckoutCart({ state, commit, dispatch }) {
      if (state.activelySubmittingCart) return
      await dispatch('getBottle', { includes: CHECKOUT_CART_INCLUDES })
      commit('CLEAR_SUBMITTABLE_CARTS')
    },

    async submitOldCartToNewBottle({ state, commit, getters }, payload) {
      // Back-compat: callers may pass a bare newBottleId string/number, OR an
      // object { newBottleId, cartItems } where cartItems is provided
      // explicitly (V2 callers — V2 stores cart_items in TanStack Query, not
      // in Vuex, so getters.submittableCart is empty there). When cartItems is
      // omitted, fall back to getters.submittableCart (V1 behaviour).
      const newBottleID = typeof payload === 'object' && payload !== null ? payload.newBottleId : payload
      const cartItems =
        typeof payload === 'object' && payload !== null && payload.cartItems
          ? payload.cartItems
          : getters.submittableCart
      commit('NEW_CART_CONTROLLER')
      try {
        state.activelySubmittingCart = true
        const res = await merchantFetch('bottles/' + newBottleID + '/carts', {
          signal: state.cartController.signal,
          method: 'POST',
          temporaryActualUltramarineEnable: true,
          includes: [
            'cart.bottle.fulfillment_slot',
            'cart.membership_tier',
            'cart.pricing_tier',
            'cart.next_pricing_tier',
            'cart.cart_items',
            'cart.cart_items.cart_item_variant_options',
            'category_product_store_pairs'
          ],
          context: { bottle: getters.bottleId },
          body: JSON.stringify({
            cart: {
              cart_items_attributes: cartItems
            }
          })
        })
        state.activelySubmittingCart = false
        commit('RECEIVE_OBJECT', res)
      } catch (e) {
        if (e.name === 'AbortError') {
          // aborted by a newer cart
        } else {
          throw e
        }
      }
    },

    async selectPass({ commit, getters }, membershipPassId) {
      const res = await merchantFetch('bottles/' + getters.bottleId, {
        method: 'PUT',
        body: JSON.stringify({
          bottle: { membership_pass_id: membershipPassId }
        }),
        includes: [
          'possible_stripe_payment_methods',
          'possible_fulfillment_methods',
          'possible_fulfillment_slot_days',
          'possible_fulfillment_slot_times',
          'possible_delivery_addresses',
          'possible_fulfillment_methods.delivery_address',
          'possible_fulfillment_methods.fulfillment_slots_detail',
          'checkout',
          'fulfillment_slot',
          'fulfillment_method',
          'cart',
          'cart.cart_items',
          'parent_bottle.children_bottles',
          'parent_bottle.children_bottles.checkout'
        ]
      })
      commit('RECEIVE_OBJECT', res)
    },

    // Customer delivery address management
    async setCustomerDeliveryAddress(
      { state, commit, getters, dispatch },
      { address1, address2, city, addressState, zip, notes, country, replacingAddressId, isDefault }
    ) {
      const url = replacingAddressId
        ? `customers/delivery_addresses/${replacingAddressId}`
        : 'customers/delivery_addresses'

      const res = await merchantFetch(url, {
        method: replacingAddressId ? 'PUT' : 'POST',
        body: JSON.stringify({
          delivery_address: {
            address1: address1,
            address2: address2,
            city: city,
            state: addressState,
            zip: zip,
            notes: notes,
            country: country,
            is_default: isDefault === true,
            replacing_address_id: replacingAddressId
          }
        }),
        includes: ['delivery_addresses']
      })
      commit('RECEIVE_OBJECT', res)
    },

    async removeCustomerDeliveryAddress({ state, commit, getters, dispatch }, { deliveryAddressID }) {
      const res = await merchantFetch(`customers/delivery_addresses/${deliveryAddressID}`, {
        method: 'DELETE',
        includes: ['delivery_addresses']
      })
      commit('RECEIVE_OBJECT', res)
    },

    async deleteStripeSource({ state, commit, getters }, stripeSourceId) {
      const res = await merchantFetch(`bottles/${getters.bottleId}/stripe_sources/${stripeSourceId}`, {
        method: 'DELETE',
        includes: ['possible_stripe_payment_methods', 'checkout']
      })
      commit('RECEIVE_OBJECT', res)
      return res
    }
  }
})

export default store
